import { useState } from "react";

import {
  ACCOUNT_DISPOSITION_STATUSES,
  ACCOUNT_STATUSES,
} from "shared/lib/constants/account";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { QC_TABLE_TOGGLES_INITIAL_STATE } from "shared/lib/constants/qualification-criteria";

import QualificationModal from "./qualification-modal";
import AccountCalendlyModal, {
  ACCOUNT_CALENDLY_MODAL_ID,
} from "./calendly-modal";
import SubmitBookedMeetingModal, {
  ACCOUNT_SUBMIT_BOOKED_MEETING_MODAL_ID,
} from "./submit-booked-meeting-modal";
import {
  SchedulingLinkModal,
  SCHEDULING_LINK_MODAL_ID,
} from "./scheduling-link-modal";
import { useFetchQualificationCriteriaAndPricing } from "@/api/routes/qualification-criteria/queries";
import { useAccountsContext } from "@/hooks/use-accounts-context";
import { CampaignI } from "@/lib/types/campaign";
import { CalendlyEventI } from "@/lib/types/dialer/calendly-event";
import { QualificationCriteriaTableTogglesValueI } from "shared/lib/interfaces/qualification-criteria";
import { useBookMeetingByCallId } from "./use-book-meeting-by-call-id";
import { useCallingContext } from "@/hooks/dialer/use-dialer-context";
import { CollapsibleDetailsSidebar } from "./collapsible-details-sidebar";
import { useQueryFetchCampaign } from "@/hooks/queries/use-query-fetch-campaign";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { useQueryFetchCalendlyURI } from "@/modals/account-book-meeting-modal/data/queries";

interface AccountBookMeetingModalPropsI {
  modalId?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

export const ACCOUNT_BOOK_MEETING_MODAL_ID = "account_book_meeting_modal";

export const AccountBookMeetingModal = ({
  modalId = ACCOUNT_BOOK_MEETING_MODAL_ID,
  onClose = () => {},
  onSuccess = () => {},
}: AccountBookMeetingModalPropsI) => {
  // NEED THIS BECAUSE CALENDLY TRIGGERS onCalendlySuccess FOR ALL RENDERED CALENDLY MODALS
  // AND WE HAVE TWO ON ACCOUNT PAGE.
  // THIS VARIABLE HELPS US RENDER IT ONLY AFTER THE QUALIFICATION CRITERIA STEP FOR THIS MODAL IS SUCCESSFUL
  const [isCalendlyModalVisible] = useState(false);
  const [isSchedulingLinkModalVisible, setIsSchedulingLinkModalVisible] =
    useState(false);
  const [calendlyEvent, setCalendlyEvent] = useState<CalendlyEventI>();
  const [bookedMeetingId, setBookedMeetingId] = useState<string>();

  const { modalAccount, clearModalAccount, updateAccountWithCampaign } =
    useAccountsContext();
  const { call } = useCallingContext();
  const { bookMeetingByCallId, updateBookedMeetingByCallId } =
    useBookMeetingByCallId();

  const { campaignId, accountId } = modalAccount || {};
  const accountName = modalAccount?.accountName as string;
  const campaign = useQueryFetchCampaign(campaignId).data?.campaign;

  const isReschedule =
    modalAccount?.accountStatus ===
    ACCOUNT_DISPOSITION_STATUSES.MEETING_SCHEDULED;

  // Use scheduling configuration from modalAccount
  const scheduling = modalAccount?.scheduling;
  const isAsyncBooking = scheduling?.is_async_booking ?? false;

  const accountCalendlyURIQuery = useQueryFetchCalendlyURI(
    campaignId,
    accountId,
    isCalendlyModalVisible
  );
  const accountCalendlyUri = accountCalendlyURIQuery.data;

  const qualificationCriteriaAndPricingApi =
    useFetchQualificationCriteriaAndPricing(campaignId);

  const [selectedQCs, setSelectedQCs] = useState(
    QC_TABLE_TOGGLES_INITIAL_STATE
  );

  const onSelectQCsSuccess = (qc: QualificationCriteriaTableTogglesValueI) => {
    setSelectedQCs(qc);

    setIsSchedulingLinkModalVisible(true);
    modalHelpers.lazyOpen(SCHEDULING_LINK_MODAL_ID);
  };

  const onSchedulingLinkSuccess = () => {
    modalHelpers.close(SCHEDULING_LINK_MODAL_ID);

    // Dispatch reload events
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD,
    });
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
    });

    clearModalAccount();
    onSuccess();
  };

  const onCalendlySuccess = (calendlyEvent: CalendlyEventI) => {
    setCalendlyEvent(calendlyEvent);

    (async () => {
      const callId = call?.customParameters.get("call_id");
      if (callId) {
        const bookedMeetingId = await bookMeetingByCallId(calendlyEvent);

        setBookedMeetingId(bookedMeetingId);
      }

      // Close current modal
      modalHelpers.close(ACCOUNT_CALENDLY_MODAL_ID);
      modalHelpers.lazyOpen(ACCOUNT_SUBMIT_BOOKED_MEETING_MODAL_ID);
    })();
  };

  /**
   * This function is used to submit the booked meeting
   * It uses 2 flows:
   * 1. If call is available, it uses the handleBookMeetingByCallId hook to book the meeting
   * 2. If call is not available, it uses the updateAccountWithCampaign hook to update the meeting
   * @param notes - The notes for the meeting
   * @returns {Promise<boolean>}
   */
  const onSubmitBookedMeetingOverview = async (notes: string) => {
    {
      if (!accountId || !campaignId) {
        return;
      }

      const callId = call?.customParameters.get("call_id");

      if (callId) {
        const isFinalized = await updateBookedMeetingByCallId(
          bookedMeetingId as string,
          notes
        );

        if (isFinalized) {
          clearModalAccount();
          onSuccess();
        }

        return;
      }

      return updateAccountWithCampaign(accountId, campaignId, {
        status: ACCOUNT_STATUSES.MEETING_SCHEDULED,
        modify_meeting_request: {
          event_uri: calendlyEvent?.id || "test",
          notes,
        },
      })
        .then(() => {})
        .finally(() => {
          clearModalAccount();
          onSuccess();
        });
    }
  };

  return (
    <>
      <QualificationModal
        modalId={modalId}
        qcApi={qualificationCriteriaAndPricingApi}
        accountName={accountName}
        onSuccess={onSelectQCsSuccess}
        onClose={onClose}
      />

      {isCalendlyModalVisible && (
        <AccountCalendlyModal
          accountName={accountName}
          contact={modalAccount?.contact}
          isLoadingCalendlyUri={accountCalendlyURIQuery.isPending}
          calendlyUri={accountCalendlyUri}
          onSuccess={onCalendlySuccess}
          onClose={onClose}
        />
      )}

      {isSchedulingLinkModalVisible && (
        <SchedulingLinkModal
          campaign={campaign}
          isReschedule={isReschedule}
          isAsyncBooking={isAsyncBooking}
          onSuccess={onSchedulingLinkSuccess}
          onClose={onClose}
        />
      )}

      {(isCalendlyModalVisible || isSchedulingLinkModalVisible) && (
        <CollapsibleDetailsSidebar
          contactId={modalAccount?.contact?.contact_id as string}
        />
      )}

      {accountCalendlyUri && (
        <SubmitBookedMeetingModal
          hideCloseAction
          modalId={ACCOUNT_SUBMIT_BOOKED_MEETING_MODAL_ID}
          calendlyUri={accountCalendlyUri}
          calendlyEvent={calendlyEvent as CalendlyEventI}
          campaign={campaign as CampaignI}
          qcApi={qualificationCriteriaAndPricingApi}
          selectedQCs={selectedQCs}
          onSubmit={onSubmitBookedMeetingOverview}
        />
      )}
    </>
  );
};
